import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Flag } from 'lucide-react';
import { reportAPI } from '../../services/api';

// Keep values in sync with backend models/Report.js REPORT_REASONS
const REASONS = [
  { value: 'inappropriate', label: 'Inappropriate or offensive content' },
  { value: 'prohibited', label: 'Prohibited or recalled item' },
  { value: 'counterfeit', label: 'Counterfeit or fake' },
  { value: 'misleading', label: 'Misleading description or photos' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'safety', label: 'Safety concern' },
  { value: 'other', label: 'Other' },
];

const ReportItemModal = ({ item, onClose }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError('Please choose a reason.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await reportAPI.createReport({
        itemId: item._id,
        reason,
        description: description.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--cardBackground)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Flag size={18} /> Report Listing
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--textSecondary)' }}>
            <X size={22} />
          </button>
        </div>

        {done ? (
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <h4 style={{ marginBottom: '8px' }}>Report submitted</h4>
            <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '20px' }}>
              Thank you for helping keep BabyResell safe. Our team will review this
              listing against our community guidelines.
            </p>
            <button onClick={onClose} className="btn btn-primary" style={{ width: '100%' }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
            <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '16px' }}>
              Reporting <strong>{item.title}</strong>. Tell us what's wrong — this is
              anonymous to the seller. See our{' '}
              <Link to="/community-guidelines" target="_blank" style={{ color: 'var(--primary)' }}>
                community guidelines
              </Link>.
            </p>

            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
              Reason *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                marginBottom: '16px',
                fontSize: '14px',
                background: 'white',
              }}
            >
              <option value="">Select a reason…</option>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>

            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
              Details (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Add any details that will help our team review this report…"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                fontSize: '14px',
                resize: 'vertical',
              }}
            />

            {error && (
              <div className="error-message" style={{ marginTop: '12px', marginBottom: 0 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button type="button" onClick={onClose} className="btn" style={{ flex: 1 }}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportItemModal;
